import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { FreemiumGate } from "@/components/FreemiumGate";
import { AuthProvider } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import RegisterPage from "@/pages/RegisterPage";
import DiscoverPage from "@/pages/DiscoverPage";
import BookDetailPage from "@/pages/BookDetailPage";
import AddBookPage from "@/pages/AddBookPage";
import MyLibraryPage from "@/pages/MyLibraryPage";
import RecommendationsPage from "@/pages/RecommendationsPage";
import AccountPage from "@/pages/AccountPage";
import AuthorPage from "@/pages/AuthorPage";
import SubscribePage from "@/pages/SubscribePage";
import SubscribeSuccessPage from "@/pages/SubscribeSuccessPage";
import FaqPage from "@/pages/FaqPage";
import HelpPage from "@/pages/HelpPage";
import ContactPage from "@/pages/ContactPage";
import WishlistPage from "@/pages/WishlistPage";
import AdminPage from "@/pages/AdminPage";
import SubAccountsPage from "@/pages/SubAccountsPage";
import MessagingPage from "@/pages/MessagingPage";
import StatsPage from "@/pages/StatsPage";
import ListsPage from "@/pages/ListsPage";
import ListDetailPage from "@/pages/ListDetailPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import PhotoScanPage from "@/pages/PhotoScanPage";
import ParentConsentPage from "@/pages/ParentConsentPage";
import PrivacyPage from "@/pages/PrivacyPage";
import LegalNoticePage from "@/pages/LegalNoticePage";
import CookiesPolicyPage from "@/pages/CookiesPolicyPage";
import TermsPage from "@/pages/TermsPage";
import ScrollToTop from "@/components/ScrollToTop";
import { SplashScreen } from "@/components/SplashScreen";
import BlogPage, { BlogPostPage } from "@/pages/BlogPage";
import VerifyEmailSentPage from "@/pages/VerifyEmailSentPage";
import SeriesPage from "@/pages/SeriesPage";
import CancelPlanPage from "@/pages/CancelPlanPage";

function Layout({ children }) {
  const { pathname } = useLocation();
  const hideFooter = ["/login", "/register"].includes(pathname);
  return (
    <>
      <SplashScreen />
      <Navbar />
      <Toaster position="top-center" richColors />
      <FreemiumGate />
      <main>{children}</main>
      {!hideFooter && <Footer />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Layout>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/discover" element={<DiscoverPage />} />
              <Route path="/books/:id" element={<BookDetailPage />} />
              <Route path="/authors/:olid" element={<AuthorPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth-callback" element={<AuthCallback />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/add" element={<ProtectedRoute><AddBookPage /></ProtectedRoute>} />
              <Route path="/scan-photo" element={<ProtectedRoute><PhotoScanPage /></ProtectedRoute>} />
              <Route path="/library" element={<ProtectedRoute><MyLibraryPage /></ProtectedRoute>} />
              <Route path="/recommendations" element={<ProtectedRoute><RecommendationsPage /></ProtectedRoute>} />
              <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
              <Route path="/wishlist" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
              <Route path="/lists" element={<ProtectedRoute><ListsPage /></ProtectedRoute>} />
              <Route path="/lists/:listId" element={<ProtectedRoute><ListDetailPage /></ProtectedRoute>} />
              <Route path="/sub-accounts" element={<ProtectedRoute><SubAccountsPage /></ProtectedRoute>} />
              <Route path="/messages" element={<ProtectedRoute><MessagingPage /></ProtectedRoute>} />
              <Route path="/messages/:threadId" element={<ProtectedRoute><MessagingPage /></ProtectedRoute>} />
              <Route path="/stats" element={<ProtectedRoute><StatsPage /></ProtectedRoute>} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/parent-consent" element={<ParentConsentPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/legal/privacidad" element={<PrivacyPage />} />
              <Route path="/legal/aviso-legal" element={<LegalNoticePage />} />
              <Route path="/legal/condiciones" element={<TermsPage />} />
              <Route path="/legal/cookies" element={<CookiesPolicyPage />} />
              <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
              <Route path="/subscribe" element={<SubscribePage />} />
              <Route path="/subscribe/success" element={<SubscribeSuccessPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/verify-email-sent" element={<VerifyEmailSentPage />} />
              <Route path="/series/:name" element={<SeriesPage />} />
              <Route path="/cancel-plan" element={<ProtectedRoute><CancelPlanPage /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
